import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useGetContactCollectionByMagicLinkQuery, useCreateContactsWithoutTagsMutation, useGetTelemetryDataForContactCollectionGuestQuery } from '@graphql/generated';
import { VisualType } from '../routes/Create/components/Editor/components/Switch/Switch';
import { getImageSource } from '../routes/Create/components/Preview/Preview';
import { useTranslation, useRouterHelper } from '@shared/core';
import { useContactCollectorGuestTelemetryTelemetry } from './ContactCollectorGuest.telemetry';
import { NATIONAL_COUNTRY, NATIONAL_COUNTRY_CODE } from '@apps/admin/common/ShippingAddress/constants';
import { contactCollectorGuestRoutePaths } from '.';

export interface ContactInputFields
  extends Readonly<{
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    email: string;
    address1: string;
    address2?: string;
    postalCode: string;
    city: string;
    region: string;
    country: string;
    countryCode: string;
    validated: boolean;
  }> {}

export type Contacts = { contacts: ContactInputFields[] };

const contactInitialValue: ContactInputFields = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  region: '',
  country: NATIONAL_COUNTRY,
  countryCode: NATIONAL_COUNTRY_CODE,
  validated: false,
};

export const newMember = {
  ...contactInitialValue,
  // address from extra members will be removed before send to BE but still mandatory in the form for head of the household
  // so we can not use empty string for these fields
  address1: ' ',
  postalCode: ' ',
  city: ' ',
  region: ' ',
};

const initialValues: Contacts = {
  contacts: [contactInitialValue],
};

export interface Context
  extends Readonly<{
    coupleNames: string;
    eventDisplayName: string;
    date: string;
    eventDateDisplayEnabled: boolean;
    website: string;
    websiteLinkEnabled: boolean;
    fontFamily: string;
    fontTextTransform: string;
    mediaType: string;
    mediaSource: string;
    firebaseId: string;
  }> {}

const yupSchema = (tRequired: string, tInvalidEmail: string) =>
  Yup.object({
    contacts: Yup.array()
      .min(1)
      .of(
        Yup.object<ContactInputFields>({
          firstName: Yup.string().required(tRequired),
          lastName: Yup.string().required(tRequired),
          phoneNumber: Yup.string(),
          email: Yup.string().email(tInvalidEmail),
          address1: Yup.string().required(tRequired),
          address2: Yup.string(),
          postalCode: Yup.string().required(tRequired),
          city: Yup.string().required(tRequired),
          region: Yup.string().required(tRequired),
          country: Yup.string(),
          countryCode: Yup.string(),
          validated: Yup.boolean(),
        }),
      ),
  });

export const useContactCollectorGuestController = (magicLinkId: string) => {
  const [e164PhoneNumber, setE164PhoneNumber] = useState<{ [key: number]: string }>({ 0: '' });
  const [context, setContext] = useState<Context>({
    coupleNames: '',
    eventDisplayName: '',
    date: '',
    eventDateDisplayEnabled: false,
    website: '',
    websiteLinkEnabled: false,
    fontFamily: 'PT Serif',
    fontTextTransform: 'none',
    mediaType: '',
    mediaSource: '',
    firebaseId: '',
  });
  const { trackError } = useContactCollectorGuestTelemetryTelemetry();
  const routeHelpers = useRouterHelper();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const { t2 } = useTranslation('ssrServer');
  const tMetatags = t2('metatags');
  const { value: contactCollectionFeatureEnabled, loading: isFeatureFlagLoading } = useFeatureValue('contactCollectionEnabled');
  const { data, error } = useGetContactCollectionByMagicLinkQuery({
    variables: {
      magicLink: magicLinkId,
    },
    batchMode: 'fast',
    ssr: true,
    fetchPolicy: 'cache-first',
    onError: error => trackError('Query', error),
  });

  const eventId = data?.getContactCollectionByMagicLink?.eventId;
  const isContactCollectionInactive = !!!data?.getContactCollectionByMagicLink?.active;

  const { data: telemetryQueryData, loading: isTelemetryQueryLoading } = useGetTelemetryDataForContactCollectionGuestQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      eventId: eventId!,
    },
    skip: !eventId,
    onError: error => trackError('Query', error),
  });

  const ogImage = getImageSource(data?.getContactCollectionByMagicLink?.mediaType as VisualType, data?.getContactCollectionByMagicLink?.mediaSource ?? '');
  const ogTitle = data?.getContactCollectionByMagicLink?.message || tMetatags.title;
  const ogUrl = `https://withjoy.com/contact/${magicLinkId}`;

  const [createContactsMutation, { loading: isCreateContactsMutationLoading, error: createContactsMutationError }] = useCreateContactsWithoutTagsMutation();

  const formik = useFormik<Contacts>({
    initialValues,
    validationSchema: yupSchema(tAdmin.required(), tAdmin.invalidEmail()),
    validateOnMount: true,
    onSubmit: async values => {
      try {
        const contactCollectionId = data?.getContactCollectionByMagicLink?.id;

        if (!contactCollectionId) {
          throw Error('No Collection Id provided');
        }

        const mutationData = values.contacts.map((contact, index) => ({
          address:
            // we do not want to save addresses from household members which are not the head of the household
            index === 0
              ? {
                  address1: contact.address1,
                  address2: contact.address2,
                  city: contact.city,
                  country: contact.country,
                  countryCode: contact.countryCode,
                  postalCode: contact.postalCode,
                  region: contact.region,
                  validated: contact.validated,
                }
              : undefined,
          contact: {
            contactCollectionId,
            firebaseEventId: context.firebaseId,
            eventId,
            email: ((contact.email || values.contacts[0].email) ?? '').trim(),
            firstName: (contact.firstName ?? '').trim(),
            lastName: (contact.lastName ?? '').trim(),
            party: values.contacts.length > 1 ? `${contactCollectionId}-${values.contacts[0].firstName}-${values.contacts[0].lastName}` : undefined,
          },
          phoneNumbers: contact.phoneNumber
            ? [
                {
                  allowSMS: true,
                  mobile: true,
                  number: contact.phoneNumber.trim(),
                },
              ]
            : undefined,
        }));
        await createContactsMutation({ variables: { data: mutationData } });
        setE164PhoneNumber({ 0: '' });
        routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.done.path);
      } catch (error) {
        trackError('Mutation', error);
      }
    },
  });

  return {
    tAdmin,
    showErrorPage: !contactCollectionFeatureEnabled || !!error,
    isContactCollectionInactive,
    eventHandle: telemetryQueryData?.eventById?.website ?? '',
    isLoading: isFeatureFlagLoading || isTelemetryQueryLoading,
    data,
    noData: data?.getContactCollectionByMagicLink === null,
    ogImage,
    ogTitle,
    ogUrl,
    formik,
    context,
    setContext,
    isMutationLoading: isCreateContactsMutationLoading,
    hasMutationError: !!createContactsMutationError,
    telemetryData: {
      postgresEventId: telemetryQueryData?.eventById?.id ?? '',
      firebaseEventId: telemetryQueryData?.eventById?.firebaseId ?? '',
      joyEventType: telemetryQueryData?.eventById?.info.eventType ?? '',
    },
    e164PhoneNumber,
    setE164PhoneNumber,
  };
};
